import { AppContext, AdaptivePracticeContext } from "../../context/context";
import { decryptData } from '../../utils/utils';
import { Component, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ContentFactoryImpl, Content } from '@mathbuddy/adaptive-practice-engine';
import { PlayFrame } from "./frame/PlayFrame";
import { ls } from "../../localStorage";
import { applyTheme, resolveTheme, Theme } from './theme';
import { Loader } from './Loader';


// View-only single-question preview for teachers: renders ONE question through the adaptive
// engine, no save / no scoring / no next-prev. Launched in an iframe from teacher-web's Question
// Analysis report and from a quiz's question-wise report.
//
// A question is named EITHER by skillId (lo_id) + questionId, which is a skill's question, OR by
// questionId alone, which is a HOTS one: a HOTS question belongs to no single skill, so it has no
// skill id to be found by - the engine looks it up by its own id instead. That is also how a quiz
// stores the two kinds, so a report can hand over what it holds without deciding anything.
//
// The payload may also carry a `reviewObj`, which is a question FROZEN: the numbers, the order of
// the options, everything that makes it the question a particular class sat. With one the engine
// replays exactly that; without one it draws the template afresh, which is right for a report
// about a question in general and wrong for a report about one quiz.


export function QuestionPreview() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY = queryParams.get("query");
  const skillId = queryParams.get("skillId") || '';
  const questionId = queryParams.get("questionId") || '';
  const lang = queryParams.get("lang") || 'en';
  const platform = queryParams.get("platform") || 'web';
  /**
   * Whether the question is only to be LOOKED at. True unless a screen says otherwise, because
   * looking is what a preview is for - a screen that wants its question answerable asks for that
   * in so many words.
   */
  const isReadOnly = queryParams.get("isReadOnly") !== 'false';
  const state: any = useContext(AppContext);

  const [currentUser, setCurrentUser] = useState<any>(null);
  // Theme chosen by whoever opened this, carried in the encrypted payload. A quiz asks for its own
  // (Dawn), so the question looks as it did when it was played.
  const [theme, setTheme] = useState<Theme | undefined>(undefined);
  /** The frozen question, when the screen that opened this holds one. */
  const [reviewObj, setReviewObj] = useState<any>(undefined);

  useEffect(() => {
    try {
      if (!QUERY) throw new Error('Missing query');
      const query: { currentUser: any, theme?: Theme, reviewObj?: any } = decryptData(QUERY);
      setTheme(query.theme);
      setReviewObj(query.reviewObj);
      const user = query.currentUser;
      if (!user?.token || !user?.dbToken) throw new Error('Invalid session');
      ls.setToken(user.token);
      ls.setDBToken(user.dbToken);
      setCurrentUser(user);
    } catch (error) {
      console.log(error);
      state.messageForParent('close');
    }
  }, []);

  if (!currentUser || questionId === '') return <Loader />;

  return (
    <QuestionPreviewPlayer
      currentUser={currentUser}
      skillId={skillId}
      questionId={questionId}
      lang={lang}
      platform={platform}
      theme={theme}
      reviewObj={reviewObj}
      isReadOnly={isReadOnly}
    />
  );
}

class QuestionPreviewPlayer extends Component<any, any> {
  static contextType = AppContext;
  context!: { windowHeight: string, windowWidth: string, messageForParent: any, navigateTo: any };

  constructor(props: any) {
    super(props);
    this.state = {
      // Optional `theme` prop from the parent; falls back to this screen's own
      // default. Resolved once - the theme cannot change between questions.
      theme: resolveTheme(props.theme),
      contentFactoryObj: new ContentFactoryImpl(),
      contentObj: new Content(),
      langObj: { number_lang: 'en', lang: '' },
      isLoader: true,
      hasError: false,
      response: { component: [] },
    };
  }

  componentDidMount = () => {
    // Paint the frame ONCE per screen - 21 custom properties, not a per-question cost.
    applyTheme(this.state.theme);
    this.loadQuestion();
  }

  loadQuestion = async () => {
    try {
      this.setState({ isLoader: true, hasError: false });
      const { skillId, questionId, lang, currentUser, reviewObj } = this.props;
      const countryCode = (currentUser.classroom_country_code || currentUser.country_code || 'IN').toLowerCase();
      const langObj = { number_lang: 'en', lang: `${lang}_${countryCode}` };

      let targetQuestionId = questionId;

      if (skillId === '') {
        // A HOTS question, named by itself. Its loader takes no theme, so the theme is stamped on
        // the engine first or it keeps whatever the last screen left behind.
        this.state.contentObj.setTheme(this.state.theme);
        const loaded: any = this.state.contentObj.loadHotsQuestionById(questionId);
        if (!loaded) throw new Error('No such HOTS question');
        targetQuestionId = loaded.id;
      } else {
        await this.state.contentObj.loadSkillBySkillId(skillId, this.state.theme, langObj.lang);

        // Find the target question's position so the engine has it loaded, then render it.
        const totalQuestions = await this.state.contentObj.getNumberOfQuestions();
        for (let pos = 0; pos < totalQuestions; pos++) {
          const q: { questionId: string } = await this.state.contentObj.loadQuestionByNumber(pos);
          if (q?.questionId?.toString() === questionId.toString()) {
            targetQuestionId = q.questionId;
            break;
          }
        }
      }

      // The frozen question when there is one, which is what makes this the question that was
      // played rather than another of its kind.
      const response = await this.state.contentFactoryObj.displayQuestion(
        this.state.contentObj,
        this.state.contentObj.createContentTemplateObject(targetQuestionId, 'REACT_WEB'),
        langObj,
        reviewObj,
      );

      this.setState({ langObj, response, isLoader: false });
    } catch (error) {
      console.log(error);
      this.setState({ isLoader: false, hasError: true });
    }
  }

  onClose = () => {
    this.context.messageForParent('close');
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="frame-container d-flex flex-column justify-content-center align-items-center" style={{ gap: '16px', color: '#fff' }}>
          <span>Couldn't load this question.</span>
          <button className="skip-btn" onClick={this.onClose}>Close</button>
        </div>
      );
    }

    return (
      <AdaptivePracticeContext.Provider value={{
        notificationMessage: '',
        coins: undefined,
        streaks: undefined,
        isSafeZone: false,
        meter: { percentage: 0, level: '' },
        isLoader: this.state.isLoader,
        isCoinAnimation: false,
        isShowStartPopup: false,
        isShowAdaptivePopup: false,
        isCloseFromClick: false,
        engineObj: this.state.response,
        options: {
          readOnly: this.props.isReadOnly === true,
          isToggleFullSceren: this.props.platform !== 'app',
          // Refresh draws the question again with new numbers, which a frozen one must not do -
          // it is being looked at precisely because it is the one that was played.
          isShowReload: !this.props.reviewObj,
          isMusicAllowed: false,
        },
        loadContent: this.loadQuestion,
        // View-only: ignore answers, never persist.
        saveQuestionResponse: async () => { },
        getPopUpInfo: () => ({}),
        // Close button closes the preview immediately (no "are you sure" popup).
        setShowAdaptivePopup: this.onClose,
        onclose: this.onClose,
      } as any}>
        <PlayFrame></PlayFrame>
      </AdaptivePracticeContext.Provider>
    );
  }
}

export default QuestionPreview;
